import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import BranchFormDrawer from "@/components/BranchFormDrawer";
import BranchReassignDialog from "@/components/BranchReassignDialog";
import {
  Plus,
  PencilSimple,
  Trash,
  Buildings,
  CheckCircle,
  XCircle,
  ArrowClockwise,
  ArrowsLeftRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STOCK_YARD_ID = "stock-yard";

function BranchRow({ branch, onEdit, onToggle, onReassign, isDefault }) {
  return (
    <tr
      data-testid={`branch-row-${branch.id}`}
      className="border-b border-canvas-line hover:bg-canvas-panel transition-colors"
    >
      <td className="px-4 py-3">
        <div className="text-sm font-semibold text-ink">{branch.name}</div>
        {branch.address && (
          <div className="text-[12px] text-ink-muted mt-0.5 line-clamp-1 max-w-md">
            {branch.address}
          </div>
        )}
        {isDefault && (
          <div className="text-[10px] font-mono uppercase tracking-wider text-brand mt-1">
            Default Branch
          </div>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-ink">{branch.code}</td>
      <td className="px-4 py-3 text-sm text-ink-muted">{branch.manager || "—"}</td>
      <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">{branch.gstin || "—"}</td>
      <td className="px-4 py-3">
        {branch.active ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase text-[#00B341]">
            <CheckCircle size={12} weight="fill" /> Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase text-ink-subtle">
            <XCircle size={12} weight="fill" /> Inactive
          </span>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">{fmtDate(branch.created_at)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 justify-end">
          <button
            onClick={() => onReassign(branch)}
            data-testid={`reassign-branch-${branch.id}`}
            className="p-1.5 border border-canvas-line hover:border-ink"
            aria-label="Move records"
            title="Move records to another branch"
          >
            <ArrowsLeftRight size={13} weight="bold" />
          </button>
          <button
            onClick={() => onEdit(branch)}
            data-testid={`edit-branch-${branch.id}`}
            className="p-1.5 border border-canvas-line hover:border-ink"
            aria-label="Edit"
          >
            <PencilSimple size={13} weight="bold" />
          </button>
          {!isDefault && (
            <button
              onClick={() => onToggle(branch)}
              data-testid={`toggle-branch-${branch.id}`}
              className={`p-1.5 border ${
                branch.active
                  ? "border-canvas-line hover:border-destructive hover:text-destructive"
                  : "border-canvas-line hover:border-ink"
              }`}
              aria-label={branch.active ? "Deactivate" : "Reactivate"}
              title={branch.active ? "Deactivate" : "Reactivate"}
            >
              {branch.active ? (
                <Trash size={13} weight="bold" />
              ) : (
                <ArrowClockwise size={13} weight="bold" />
              )}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function UserAssignmentRow({ u, branches, onAssign, saving }) {
  return (
    <tr
      data-testid={`user-row-${u.id}`}
      className="border-b border-canvas-line hover:bg-canvas-panel transition-colors"
    >
      <td className="px-4 py-3">
        <div className="text-sm font-semibold text-ink">{u.name}</div>
        <div className="font-mono text-[11px] text-ink-muted">{u.email}</div>
      </td>
      <td className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-ink-muted">
        {u.role}
      </td>
      <td className="px-4 py-3">
        {u.role === "admin" ? (
          <span className="font-mono text-[11px] text-ink-subtle italic">All branches</span>
        ) : (
          <select
            data-testid={`assign-branch-${u.id}`}
            value={u.branch_id || ""}
            disabled={saving}
            onChange={(e) => onAssign(u, e.target.value || null)}
            className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          >
            <option value="">Unassigned</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        )}
      </td>
    </tr>
  );
}

export default function BranchesPage() {
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reassignSrc, setReassignSrc] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, u] = await Promise.all([
        api.get("/branches"),
        api.get("/auth/users"),
      ]);
      setBranches(b.data || []);
      setUsers(u.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (b) => {
    setEditing(b);
    setShowForm(true);
  };
  const handleSaved = () => {
    setShowForm(false);
    load();
  };

  const handleToggle = async (b) => {
    try {
      if (b.active) {
        if (!window.confirm(`Deactivate branch "${b.name}"? It will be hidden from filters and forms.`)) return;
        await api.delete(`/branches/${b.id}`);
        toast.success("Branch deactivated");
      } else {
        await api.post(`/branches/${b.id}/reactivate`);
        toast.success("Branch reactivated");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleAssign = async (u, branchId) => {
    setAssigning(true);
    try {
      await api.patch(`/auth/users/${u.id}/branch`, { branch_id: branchId });
      toast.success(`Updated branch for ${u.name}`);
      setUsers((rows) => rows.map((row) => (row.id === u.id ? { ...row, branch_id: branchId } : row)));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAssigning(false);
    }
  };

  const activeBranches = branches.filter((b) => b.active);
  const staffUsers = users.filter((u) => u.role === "staff");
  const adminUsers = users.filter((u) => u.role === "admin");

  return (
    <div>
      <PageHeader
        eyebrow="05 / Branches"
        title="Branch Network"
        subtitle="Each branch has its own login. Staff users see only the inventory, customers, and sales for their assigned branch."
        actions={
          <button
            onClick={openCreate}
            data-testid="add-branch-button"
            className="bg-ink text-white px-4 py-2.5 text-sm font-semibold tracking-tight hover:bg-brand transition-colors duration-150 flex items-center gap-2"
          >
            <Plus size={14} weight="bold" />
            Add Branch
          </button>
        }
      />

      <div className="px-8 py-6 space-y-8">
        <section>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-3">
            All Branches
          </div>
          <div className="bg-canvas-card border border-canvas-line overflow-hidden">
            <table className="w-full" data-testid="branches-table">
              <thead>
                <tr className="border-b border-canvas-line bg-canvas-panel">
                  {["Branch", "Code", "Manager", "GSTIN", "Status", "Created", ""].map((h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && branches.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <Buildings size={24} weight="bold" className="text-ink-subtle mx-auto mb-2" />
                      <div className="font-mono text-xs text-ink-subtle">No branches yet.</div>
                    </td>
                  </tr>
                )}
                {branches.map((b) => (
                  <BranchRow
                    key={b.id}
                    branch={b}
                    onEdit={openEdit}
                    onToggle={handleToggle}
                    onReassign={setReassignSrc}
                    isDefault={b.id === STOCK_YARD_ID}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-3">
            Staff Branch Assignments
          </div>
          <div className="bg-canvas-card border border-canvas-line overflow-hidden">
            <table className="w-full" data-testid="user-branch-table">
              <thead>
                <tr className="border-b border-canvas-line bg-canvas-panel">
                  {["User", "Role", "Assigned Branch"].map((h) => (
                    <th
                      key={h}
                      className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...adminUsers, ...staffUsers].map((u) => (
                  <UserAssignmentRow
                    key={u.id}
                    u={u}
                    branches={activeBranches}
                    onAssign={handleAssign}
                    saving={assigning}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <BranchFormDrawer
        open={showForm}
        editing={editing}
        onClose={() => setShowForm(false)}
        onSaved={handleSaved}
      />

      <BranchReassignDialog
        open={!!reassignSrc}
        source={reassignSrc}
        branches={branches}
        onClose={() => setReassignSrc(null)}
        onDone={() => {
          setReassignSrc(null);
          load();
        }}
      />
    </div>
  );
}
